// 系统设置 / 报表 / 淘宝店三个模块共用的数据钩子（只读期：只走 api.get）。
// 写法照订单模块 api.ts：切换条件时 abort 旧请求，避免竞态。建议由主会话提取到 src/lib 或 src/api。
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { api, type ApiError } from '@/api/client';
import type { Page } from '@/api/types';

type Query = Record<string, string | number | undefined>;

/** 单个资源：path 为 null 时不请求（例如没有权限） */
export function useApi<T>(path: string | null, query?: Query) {
  const [state, setState] = useState<{ data?: T; loading: boolean; error?: Error }>({ loading: !!path });
  const [nonce, setNonce] = useState(0);
  const key = JSON.stringify(query || {});
  useEffect(() => {
    if (!path) {
      setState({ loading: false });
      return;
    }
    const c = new AbortController();
    setState((s) => ({ ...s, loading: true, error: undefined }));
    api
      .get<T>(path, JSON.parse(key), c.signal)
      .then((data) => setState({ data, loading: false }))
      .catch((e) => {
        if ((e as Error).name !== 'AbortError') setState({ loading: false, error: e as Error });
      });
    return () => c.abort();
  }, [path, key, nonce]);
  return { ...state, reload: () => setNonce((n) => n + 1) };
}

type PagedState<T> = { items: T[]; total: number; page: number; hasNext: boolean; loading: boolean; error?: ApiError | Error };

/**
 * 分页列表（老接口统一的 { items, pagination }）。
 * append=true：手机 / iPad 滚到底追加下一页；false：桌面翻页替换。
 */
export function usePaged<T>(path: string | null, query: Query, pageSize: number, append: boolean) {
  const [state, setState] = useState<PagedState<T>>({ items: [], total: 0, page: 1, hasNext: false, loading: !!path });
  const ctrl = useRef<AbortController | null>(null);
  const key = JSON.stringify(query);

  const fetchPage = useCallback(
    async (page: number, reset: boolean) => {
      if (!path) {
        setState({ items: [], total: 0, page: 1, hasNext: false, loading: false });
        return;
      }
      ctrl.current?.abort();
      const c = new AbortController();
      ctrl.current = c;
      setState((s) => ({ ...s, loading: true, error: undefined, ...(reset ? { items: [] } : {}) }));
      try {
        const data = await api.get<Page<T>>(path, { ...JSON.parse(key), page, pageSize }, c.signal);
        setState((s) => ({
          items: reset || !append ? data.items : [...s.items, ...data.items],
          total: data.pagination.total,
          page: data.pagination.page,
          hasNext: data.pagination.hasNext,
          loading: false,
        }));
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        setState((s) => ({ ...s, loading: false, error: e as Error }));
      }
    },
    [path, key, pageSize, append],
  );

  useEffect(() => {
    fetchPage(1, true);
    return () => ctrl.current?.abort();
  }, [fetchPage]);

  return {
    ...state,
    pageSize,
    reload: () => fetchPage(1, true),
    loadMore: () => {
      if (state.hasNext && !state.loading) fetchPage(state.page + 1, false);
    },
    goto: (p: number) => fetchPage(p, true),
  };
}

/** 滚到底自动加载下一页（手机 / iPad 卡片列表） */
export function useAutoMore(enabled: boolean, loadMore: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    const io = new IntersectionObserver((es) => es[0].isIntersecting && loadMore(), { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  });
  return ref;
}

/** 视图、筛选、搜索、分页都写进 URL 查询参数（replace，不堆历史） */
export function useUrlState() {
  const [sp, setSp] = useSearchParams();
  const set = useCallback(
    (patch: Record<string, string | undefined | null>) => {
      setSp(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v) next.set(k, v);
            else next.delete(k);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSp],
  );
  return [sp, set] as const;
}

/** 返回：站内有上一页就退回，否则去兜底地址（直接打开深链时不会退出应用） */
export function useBack(fallback: string) {
  const nav = useNavigate();
  return () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) nav(-1);
    else nav(fallback);
  };
}
